import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Spinner, Empty } from "../ui";
import { Megaphone, X, CheckCircle2, XCircle, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Harta tara -> limba asteptata (pentru semnalarea nepotrivirilor tara/limba).
const EXPECTED_LANG = {
  romania: "ro", moldova: "ro",
  italia: "it", italy: "it",
  franta: "fr", france: "fr",
  spania: "es", spain: "es",
  germania: "de", germany: "de", austria: "de",
  olanda: "nl", netherlands: "nl", "tarile de jos": "nl",
  belgia: "nl", belgium: "nl",
  portugalia: "pt", portugal: "pt",
  "marea britanie": "en", "regatul unit": "en", uk: "en", "united kingdom": "en",
  irlanda: "en", ireland: "en",
  "statele unite": "en", sua: "en", usa: "en",
};

function norm(s) {
  return (s || "").trim().toLowerCase();
}

function langMismatch(row) {
  const tara = norm(row.tara);
  const lang = norm(row.lang);
  const exp = EXPECTED_LANG[tara];
  if (!exp || !lang || lang === "-") return false;
  return exp !== lang;
}

function YesNo({ v, testid }) {
  return v ? (
    <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" data-testid={testid} />
  ) : (
    <XCircle className="w-4 h-4 text-gray-300 inline" data-testid={testid} />
  );
}

function MessageModal({ row, onClose }) {
  const mismatch = langMismatch(row);
  const body = row.message || null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-auto bg-white border border-gray-300 rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="campanii-message-modal" style={{ fontFamily: "Arial" }}>
        <div className="sticky top-0 bg-white border-b border-gray-300 px-5 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-black">Mesaj campanie</h3>
          <button onClick={onClose} data-testid="campanii-message-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-4 text-black" style={{ fontSize: "14px" }}>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-gray-500">Firma</span><div className="font-bold">{row.firma || "-"}</div></div>
            <div><span className="text-gray-500">Adresa mail</span><div className="font-bold break-all">{row.email || "-"}</div></div>
            <div><span className="text-gray-500">Tara</span><div className="font-bold">{row.tara || "-"}</div></div>
            <div>
              <span className="text-gray-500">Limba mesaj</span>
              <div className="font-bold" data-testid="campanii-message-lang">
                {row.lang || "-"}{row.message_lang_label ? ` (${row.message_lang_label})` : ""}
              </div>
            </div>
            <div className="col-span-2"><span className="text-gray-500">Subiect</span><div className="font-bold">{row.subject || "-"}</div></div>
            <div className="col-span-2">
              <span className="text-gray-500">Link activare cont</span>
              {row.activation_url ? (
                <div className="font-bold break-all">
                  <a href={row.activation_url} target="_blank" rel="noreferrer"
                    className="underline text-black hover:text-gray-600" data-testid="campanii-activation-link">
                    {row.activation_url}
                  </a>
                </div>
              ) : (
                <div className="font-bold text-gray-500" data-testid="campanii-activation-link">-</div>
              )}
            </div>
          </div>

          {mismatch && (
            <div className="flex items-start gap-2 border border-red-500 bg-red-50 p-3 rounded" data-testid="campanii-lang-mismatch">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-red-700">
                ATENTIE: limba mesajului ({row.lang}) nu corespunde tarii adresei ({row.tara}).
                Verificati maparea tara / limba in motorul de trimitere.
              </div>
            </div>
          )}

          <div>
            <div className="text-gray-500 mb-1">Text mesaj trimis</div>
            {body ? (
              <pre className="whitespace-pre-wrap font-sans border border-gray-300 rounded p-3 bg-gray-50" data-testid="campanii-message-body">{body}</pre>
            ) : (
              <div className="text-gray-500 border border-gray-300 rounded p-3" data-testid="campanii-message-body">
                - (nu exista sablon salvat pentru limba "{row.lang}")
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Campanii() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [online, setOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [campFilter, setCampFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaign-report");
      setItems(data.items || []);
      setCampaigns(data.campaigns || []);
      setOnline(!!data.delivery_source_online);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((r) => {
    const okQ = !q || (r.firma || "").toLowerCase().includes(q.toLowerCase())
      || (r.email || "").toLowerCase().includes(q.toLowerCase());
    const okC = !campFilter || r.campaign === campFilter;
    return okQ && okC;
  });

  useEffect(() => { setPage(0); }, [q, campFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages - 1);
  const start = curPage * pageSize;
  const paged = filtered.slice(start, start + pageSize);

  const cell = "px-3 py-2 border-b border-gray-200 text-black align-top";
  const head = "px-3 py-2 border-b-2 border-gray-400 font-bold text-black text-left whitespace-nowrap";

  return (
    <div style={{ fontFamily: "Arial", fontSize: "14px" }}>
      <PageHeader title="CAMPANII" subtitle="Outreach Yvola: livrabilitate si conversie per destinatar. Doar super admin." />

      {loading ? <Spinner /> : (
        <>
          {!online && (
            <div className="flex items-start gap-2 border border-gray-400 bg-gray-50 p-3 rounded mb-4" data-testid="campanii-offline-note">
              <AlertTriangle className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <div className="text-gray-700">
                Datele de livrare (livrat, bounce, blocked, complaint, dezabonat, tara) nu au putut fi citite de pe serverul de campanii.
                Coloanele afecteaza afiseaza "-" pana la restabilirea legaturii.
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cauta dupa firma sau email"
                data-testid="campanii-search"
                className="w-full pl-9 pr-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
                style={{ fontFamily: "Arial", fontSize: "14px" }} />
            </div>
            <select value={campFilter} onChange={(e) => setCampFilter(e.target.value)} data-testid="campanii-campaign-filter"
              className="px-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
              style={{ fontFamily: "Arial", fontSize: "14px" }}>
              <option value="">Toate campaniile</option>
              {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {filtered.length === 0 ? (
            <Card><Empty icon={Megaphone} title="Nicio inregistrare" hint="Nu exista destinatari in campanii inca." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap" data-testid="campanii-table" style={{ fontSize: "14px" }}>
                  <thead>
                    <tr>
                      <th className={head}>Nume campanie</th>
                      <th className={head}>Tara</th>
                      <th className={head}>Judet</th>
                      <th className={head}>Nume firma</th>
                      <th className={head}>Mesaj</th>
                      <th className={head}>Adresa mail</th>
                      <th className={head + " text-center"}>Livrat</th>
                      <th className={head + " text-center"}>Dezabonat</th>
                      <th className={head + " text-center"}>Bounce</th>
                      <th className={head + " text-center"}>Complaint</th>
                      <th className={head + " text-center"}>Blocked</th>
                      <th className={head + " text-center"}>Deschideri</th>
                      <th className={head + " text-center"}>Link accesat</th>
                      <th className={head + " text-center"}>Creat cont</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((r, i) => {
                      const mism = langMismatch(r);
                      return (
                        <tr key={i} data-testid={`campanii-row-${i}`} className="hover:bg-gray-50">
                          <td className={cell}>{r.campaign || "-"}</td>
                          <td className={cell}>
                            {r.tara || "-"}
                            {mism && <AlertTriangle className="w-3.5 h-3.5 text-red-600 inline ml-1" title="Limba nu corespunde tarii" />}
                          </td>
                          <td className={cell}>{r.judet || "-"}</td>
                          <td className={cell}>{r.firma || "-"}</td>
                          <td className={cell}>
                            <button onClick={() => setSel(r)} data-testid={`campanii-message-btn-${i}`}
                              className="underline text-black hover:text-gray-600">Vezi mesaj</button>
                          </td>
                          <td className={cell + " break-all"} style={{ whiteSpace: "normal" }}>{r.email || "-"}</td>
                          <td className={cell + " text-center"}><YesNo v={r.delivered} testid={`campanii-delivered-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.unsubscribed} testid={`campanii-unsub-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.bounced} testid={`campanii-bounce-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.complained} testid={`campanii-complaint-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.blocked} testid={`campanii-blocked-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.opened} testid={`campanii-opened-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.link_opened} testid={`campanii-linkopened-${i}`} /></td>
                          <td className={cell + " text-center"}><YesNo v={r.account_created} testid={`campanii-account-${i}`} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-gray-500" data-testid="campanii-count">
              Total destinatari: {filtered.length}
              {filtered.length > 0 && (
                <span> (afisez {start + 1}-{Math.min(start + pageSize, filtered.length)})</span>
              )}
            </div>
            {filtered.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-gray-700">
                  Pe pagina
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    data-testid="campanii-page-size"
                    className="px-2 py-1 rounded border border-gray-300 outline-none focus:border-black text-black"
                    style={{ fontFamily: "Arial", fontSize: "14px" }}>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                  </select>
                </label>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={curPage <= 0}
                    data-testid="campanii-prev-page"
                    className="px-3 py-1 rounded border border-gray-300 text-black disabled:opacity-40 hover:bg-gray-100">
                    Inapoi
                  </button>
                  <span data-testid="campanii-page-info" className="text-gray-700">
                    Pagina {curPage + 1} / {totalPages}
                  </span>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={curPage >= totalPages - 1}
                    data-testid="campanii-next-page"
                    className="px-3 py-1 rounded border border-gray-300 text-black disabled:opacity-40 hover:bg-gray-100">
                    Inainte
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {sel && <MessageModal row={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
